import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, Tooltip, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Legend } from 'recharts';
import AdminSidebar from '../../components/admin/AdminSidebar';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent } from '../../components/ui/card';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Sparkles, Plus, Edit, Trash2, TrendingUp, Target, CheckCircle2, Clock, AlertOctagon, FileText, Copy, Printer, Link as LinkIcon, ShieldOff, History, Eye, Download } from 'lucide-react';

const PRIORITIES = [
  { v: 'high', l: 'Alta', c: 'bg-red-100 text-red-800 border-red-200' },
  { v: 'medium', l: 'Média', c: 'bg-amber-100 text-amber-800 border-amber-200' },
  { v: 'low', l: 'Baixa', c: 'bg-blue-100 text-blue-800 border-blue-200' },
];
const STATUSES = [
  { v: 'pending', l: 'Pendente', c: 'bg-gray-100 text-gray-700', icon: Clock },
  { v: 'in_progress', l: 'Em Andamento', c: 'bg-blue-100 text-blue-800', icon: TrendingUp },
  { v: 'completed', l: 'Concluída', c: 'bg-emerald-100 text-emerald-800', icon: CheckCircle2 },
  { v: 'blocked', l: 'Bloqueada', c: 'bg-red-100 text-red-800', icon: AlertOctagon },
];

const COLORS = ['#0d9488', '#f59e0b', '#3b82f6', '#ef4444', '#8b5cf6', '#ec4899', '#10b981'];

const EMPTY_ACTION = {
  dimension_key: '',
  title: '',
  description: '',
  kpi_label: '',
  kpi_unit: '%',
  current_value: 0,
  target_value: 100,
  priority: 'medium',
  status: 'pending',
  responsible: '',
  due_date: '',
};

const KpiCard = ({ label, value, sub, icon: Icon, color = 'teal' }) => (
  <Card className="border-0 shadow-sm">
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold">{label}</div>
          <div className="text-3xl font-bold text-gray-900 mt-1">{value}</div>
          {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
        </div>
        <div className={`w-10 h-10 rounded-lg bg-${color}-100 text-${color}-700 flex items-center justify-center`}><Icon size={20} /></div>
      </div>
    </CardContent>
  </Card>
);

export default function ExecutiveDashboardPage() {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [assessments, setAssessments] = useState([]);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [dimensionsCatalog, setDimensionsCatalog] = useState([]);
  const [actions, setActions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  // Action dialog
  const [actionDialog, setActionDialog] = useState(false);
  const [editingAction, setEditingAction] = useState(null);
  const [actionForm, setActionForm] = useState(EMPTY_ACTION);
  const [deleteId, setDeleteId] = useState(null);

  // Report
  const [reportOpen, setReportOpen] = useState(false);
  const [report, setReport] = useState(null);
  const [reportVersionId, setReportVersionId] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [generating, setGenerating] = useState(false);

  // Client portal link
  const [tokenDialog, setTokenDialog] = useState(false);
  const [tokenInfo, setTokenInfo] = useState(null);
  const [tokenLoading, setTokenLoading] = useState(false);

  // Report history
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyVersions, setHistoryVersions] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [deleteVersionId, setDeleteVersionId] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const loadCompanies = useCallback(async () => {
    try {
      const [cRes, catRes] = await Promise.all([
        axios.get(`${API_URL}/api/companies/`, { withCredentials: true }),
        axios.get(`${API_URL}/api/assessments/dimensions/catalog`, { withCredentials: true }),
      ]);
      setCompanies(cRes.data);
      setDimensionsCatalog(catRes.data.dimensions);
      if (cRes.data.length && !selectedCompany) setSelectedCompany(cRes.data[0].id);
    } catch { toast.error('Erro ao carregar'); }
  }, [API_URL, selectedCompany]);

  const loadData = useCallback(async () => {
    if (!selectedCompany) return;
    setLoading(true);
    try {
      const [aRes, actRes, sumRes] = await Promise.all([
        axios.get(`${API_URL}/api/assessments/`, { withCredentials: true, params: { company_id: selectedCompany } }),
        axios.get(`${API_URL}/api/action-items/`, { withCredentials: true, params: { company_id: selectedCompany } }),
        axios.get(`${API_URL}/api/action-items/summary/${selectedCompany}`, { withCredentials: true }),
      ]);
      setAssessments(aRes.data);
      setActions(actRes.data);
      setSummary(sumRes.data);
      if (aRes.data.length) {
        setSelectedAssessment(aRes.data[0]);
      } else {
        setSelectedAssessment(null);
      }
    } catch { toast.error('Erro ao carregar dados'); }
    finally { setLoading(false); }
  }, [API_URL, selectedCompany]);

  useEffect(() => { loadCompanies(); }, [loadCompanies]);
  useEffect(() => { loadData(); }, [loadData]);

  const radarData = useMemo(() => {
    if (!selectedAssessment || !dimensionsCatalog.length) return [];
    return dimensionsCatalog.map((d, idx) => ({
      dimension: `${idx + 1}. ${d.label.split(' ').slice(0, 2).join(' ')}`,
      score: selectedAssessment.dimensions?.[d.key]?.score || 0,
      fullMark: 4,
    }));
  }, [selectedAssessment, dimensionsCatalog]);

  const statusPieData = useMemo(() => {
    if (!summary) return [];
    return STATUSES.map((s) => ({ name: s.l, value: summary.by_status[s.v] || 0 })).filter((x) => x.value > 0);
  }, [summary]);

  const priorityBarData = useMemo(() => {
    if (!summary) return [];
    return PRIORITIES.map((p) => ({ name: p.l, value: summary.by_priority[p.v] || 0 }));
  }, [summary]);

  const openNewAction = () => {
    setEditingAction(null);
    setActionForm({ ...EMPTY_ACTION, dimension_key: dimensionsCatalog[0]?.key || '' });
    setActionDialog(true);
  };

  const editAction = (a) => {
    setEditingAction(a);
    setActionForm({ ...EMPTY_ACTION, ...a, due_date: a.due_date || '' });
    setActionDialog(true);
  };

  const submitAction = async (e) => {
    e.preventDefault();
    if (!actionForm.title.trim() || !actionForm.dimension_key) {
      toast.error('Título e dimensão obrigatórios');
      return;
    }
    try {
      const payload = { ...actionForm, company_id: selectedCompany };
      if (selectedAssessment) payload.assessment_id = selectedAssessment.id;
      ['current_value', 'target_value'].forEach((k) => {
        if (payload[k] === '' || payload[k] === null) delete payload[k];
        else payload[k] = parseFloat(payload[k]);
      });
      if (!payload.due_date) delete payload.due_date;
      if (editingAction) {
        await axios.put(`${API_URL}/api/action-items/${editingAction.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/action-items/`, payload, { withCredentials: true });
      }
      toast.success('Salvo');
      setActionDialog(false);
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erro'); }
  };

  const deleteAction = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/action-items/${id}`, { withCredentials: true });
      toast.success('Removida');
      loadData();
    } catch { toast.error('Erro'); }
    finally { setDeleteId(null); }
  };

  const generateReport = async () => {
    if (!selectedAssessment) { toast.error('Selecione um diagnóstico'); return; }
    setGenerating(true);
    setReportOpen(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/assessments/${selectedAssessment.id}/generate-report`,
        { include_personnel: true },
        { withCredentials: true, timeout: 120000 }
      );
      setReport(data.report);
      setReportVersionId(data.version_id || null);
      toast.success('Relatório gerado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao gerar');
      setReportOpen(false);
    } finally { setGenerating(false); }
  };

  const copyReport = () => {
    if (report) {
      navigator.clipboard.writeText(report);
      toast.success('Copiado');
    }
  };

  const printReport = () => window.print();

  const openTokenDialog = async () => {
    if (!selectedCompany) return;
    setTokenDialog(true);
    setTokenLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/companies/${selectedCompany}/access-token`, { withCredentials: true });
      setTokenInfo(data);
    } catch { toast.error('Erro ao carregar'); }
    finally { setTokenLoading(false); }
  };

  const generateToken = async () => {
    setTokenLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/companies/${selectedCompany}/access-token`, {}, { withCredentials: true });
      setTokenInfo(data);
      toast.success('Link gerado');
    } catch { toast.error('Erro'); }
    finally { setTokenLoading(false); }
  };

  const revokeToken = async () => {
    setTokenLoading(true);
    try {
      await axios.delete(`${API_URL}/api/companies/${selectedCompany}/access-token`, { withCredentials: true });
      setTokenInfo({ token: '', url_path: '', enabled: false });
      toast.success('Acesso revogado');
    } catch { toast.error('Erro'); }
    finally { setTokenLoading(false); }
  };

  const clientFullUrl = tokenInfo?.url_path ? `${window.location.origin}${tokenInfo.url_path}` : '';
  const copyClientLink = () => {
    if (clientFullUrl) { navigator.clipboard.writeText(clientFullUrl); toast.success('Link copiado'); }
  };

  const openHistory = async () => {
    if (!selectedAssessment) return;
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/assessments/${selectedAssessment.id}/reports`, { withCredentials: true });
      setHistoryVersions(data.versions || []);
    } catch { toast.error('Erro ao carregar histórico'); }
    finally { setHistoryLoading(false); }
  };

  const openVersion = async (versionId) => {
    setHistoryOpen(false);
    setReportOpen(true);
    setGenerating(true);
    setReportVersionId(versionId);
    try {
      const { data } = await axios.get(`${API_URL}/api/reports/${versionId}`, { withCredentials: true });
      setReport(data.content);
    } catch { toast.error('Erro ao abrir'); }
    finally { setGenerating(false); }
  };

  const deleteVersion = async (versionId) => {
    try {
      await axios.delete(`${API_URL}/api/reports/${versionId}`, { withCredentials: true });
      toast.success('Relatório excluído');
      setDeleteVersionId(null);
      // reload history + refresh dashboard data
      openHistory();
      loadData();
    } catch { toast.error('Erro ao excluir'); }
  };

  const downloadPdf = async () => {
    if (!reportVersionId) return;
    setDownloadingPdf(true);
    try {
      const res = await axios.get(`${API_URL}/api/reports/${reportVersionId}/pdf`, {
        withCredentials: true,
        responseType: 'blob',
        timeout: 120000,
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const cname = companies.find((c) => c.id === selectedCompany)?.trade_name || 'relatorio';
      link.setAttribute('download', `diagnostico-inclusao-${cname}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF gerado');
    } catch {
      toast.error('Não foi possível gerar o PDF');
    } finally { setDownloadingPdf(false); }
  };

  const dimLabel = (key) => dimensionsCatalog.find((d) => d.key === key)?.label || key;
  const progressPct = (a) => {
    if (!a.target_value || a.target_value === 0) return 0;
    const pct = ((a.current_value || 0) / a.target_value) * 100;
    return Math.min(100, Math.max(0, pct));
  };

  return (
    <AdminSidebar
      title="Painel Executivo"
      subtitle="Diagnóstico consolidado, plano de ação e KPIs para apresentação ao cliente"
    >
      {/* Company + Assessment selectors */}
      <Card className="mb-6">
        <CardContent className="p-4 flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[220px]">
            <Label className="text-xs">Empresa Cliente</Label>
            <select
              value={selectedCompany}
              onChange={(e) => setSelectedCompany(e.target.value)}
              className="w-full h-10 px-3 rounded-md border border-input bg-white text-sm mt-1"
              data-testid="dashboard-company-select"
            >
              <option value="">Selecione...</option>
              {companies.map((c) => <option key={c.id} value={c.id}>{c.trade_name || c.legal_name}</option>)}
            </select>
          </div>
          {assessments.length > 0 && (
            <div className="flex-1 min-w-[220px]">
              <Label className="text-xs">Diagnóstico ativo</Label>
              <select
                value={selectedAssessment?.id || ''}
                onChange={(e) => setSelectedAssessment(assessments.find((a) => a.id === e.target.value))}
                className="w-full h-10 px-3 rounded-md border border-input bg-white text-sm mt-1"
                data-testid="dashboard-assessment-select"
              >
                {assessments.map((a) => (
                  <option key={a.id} value={a.id}>{a.title} · {a.overall_score.toFixed(2)}</option>
                ))}
              </select>
            </div>
          )}
          {selectedAssessment && (
            <>
              <Button onClick={generateReport} disabled={generating} className="bg-purple-600 hover:bg-purple-700" data-testid="generate-report-button">
                <Sparkles className="mr-2 h-4 w-4" /> {generating ? 'Gerando...' : 'Gerar Relatório IA'}
              </Button>
              <Button onClick={openHistory} variant="outline" data-testid="history-button">
                <History className="mr-2 h-4 w-4" /> Histórico
              </Button>
            </>
          )}
          {selectedCompany && (
            <Button onClick={openTokenDialog} variant="outline" data-testid="client-link-button">
              <LinkIcon className="mr-2 h-4 w-4" /> Link do Cliente
            </Button>
          )}
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div></div>
      ) : !selectedCompany ? (
        <Card><CardContent className="p-12 text-center text-gray-500">Selecione uma empresa.</CardContent></Card>
      ) : (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="dashboard-kpis">
            <KpiCard label="Score de Maturidade" value={selectedAssessment ? selectedAssessment.overall_score.toFixed(2) : '—'} sub={selectedAssessment?.maturity_stage || 'Sem diagnóstico'} icon={Target} color="teal" />
            <KpiCard label="Ações no Plano" value={summary?.total || 0} sub={`${summary?.completion_rate || 0}% concluído`} icon={FileText} color="blue" />
            <KpiCard label="Em Andamento" value={summary?.by_status?.in_progress || 0} sub={`${summary?.by_status?.completed || 0} concluídas`} icon={TrendingUp} color="amber" />
            <KpiCard label="Alta Prioridade" value={summary?.by_priority?.high || 0} sub="ações críticas" icon={AlertOctagon} color="red" />
          </div>

          {/* Charts row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6" data-testid="dashboard-charts">
            {/* Radar */}
            <Card className="lg:col-span-2">
              <CardContent className="p-5">
                <h3 className="text-sm font-bold text-gray-800 mb-3 uppercase tracking-wider">Mapa de Maturidade (12 Dimensões)</h3>
                {radarData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={340}>
                    <RadarChart data={radarData}>
                      <PolarGrid stroke="#e5e7eb" />
                      <PolarAngleAxis dataKey="dimension" tick={{ fontSize: 10, fill: '#374151' }} />
                      <PolarRadiusAxis angle={90} domain={[0, 4]} tick={{ fontSize: 10 }} />
                      <Radar name="Score" dataKey="score" stroke="#0d9488" fill="#0d9488" fillOpacity={0.35} strokeWidth={2} />
                      <Tooltip contentStyle={{ borderRadius: 8, borderColor: '#0d9488' }} />
                    </RadarChart>
                  </ResponsiveContainer>
                ) : <div className="text-center text-gray-400 py-12 text-sm">Sem diagnóstico ativo</div>}
              </CardContent>
            </Card>

            {/* Status Pie */}
            <Card>
              <CardContent className="p-5">
                <h3 className="text-sm font-bold text-gray-800 mb-3 uppercase tracking-wider">Distribuição de Status</h3>
                {statusPieData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie data={statusPieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={(e) => e.value}>
                        {statusPieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : <div className="text-center text-gray-400 py-12 text-sm">Sem ações</div>}
              </CardContent>
            </Card>
          </div>

          {/* Priority bar */}
          <Card className="mb-6">
            <CardContent className="p-5">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wider">Plano de Ação por Prioridade</h3>
                <Button size="sm" onClick={openNewAction} data-testid="new-action-button"><Plus className="mr-1 h-3 w-3" /> Nova Ação</Button>
              </div>
              {priorityBarData.some((p) => p.value > 0) ? (
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={priorityBarData} layout="vertical">
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={70} />
                    <Tooltip />
                    <Bar dataKey="value" fill="#0d9488" radius={[0, 6, 6, 0]}>
                      {priorityBarData.map((_, i) => <Cell key={i} fill={['#ef4444', '#f59e0b', '#3b82f6'][i]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : <div className="text-center text-gray-400 py-6 text-sm">Nenhuma ação cadastrada. Clique em &quot;Nova Ação&quot;.</div>}
            </CardContent>
          </Card>

          {/* Kanban de ações */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="action-kanban">
            {STATUSES.map((st) => {
              const Icon = st.icon;
              const list = actions.filter((a) => a.status === st.v);
              return (
                <div key={st.v}>
                  <div className={`flex items-center justify-between px-3 py-2 rounded-t-lg ${st.c}`}>
                    <div className="flex items-center gap-1.5 text-xs font-bold uppercase">
                      <Icon size={12} /> {st.l}
                    </div>
                    <span className="text-xs font-bold">{list.length}</span>
                  </div>
                  <div className="bg-gray-50 rounded-b-lg p-2 space-y-2 min-h-[200px]">
                    {list.length === 0 ? (
                      <div className="text-center text-xs text-gray-400 py-6 italic">Vazio</div>
                    ) : list.map((a) => {
                      const pr = PRIORITIES.find((p) => p.v === a.priority);
                      const pct = progressPct(a);
                      return (
                        <div key={a.id} className="bg-white rounded-lg p-3 border border-gray-200 hover:shadow-md transition-shadow" data-testid={`action-card-${a.id}`}>
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <div className="text-xs font-semibold text-gray-900 flex-1">{a.title}</div>
                            <div className="flex gap-0.5 shrink-0">
                              <button onClick={() => editAction(a)} className="text-gray-400 hover:text-teal-600" data-testid={`edit-action-${a.id}`}><Edit size={12} /></button>
                              <button onClick={() => setDeleteId(a.id)} className="text-gray-400 hover:text-red-600" data-testid={`delete-action-${a.id}`}><Trash2 size={12} /></button>
                            </div>
                          </div>
                          <div className="text-[10px] text-teal-700 font-semibold uppercase mb-2">{dimLabel(a.dimension_key)}</div>
                          {a.kpi_label && (
                            <div className="mb-2">
                              <div className="flex justify-between text-[10px] text-gray-600 mb-1">
                                <span>{a.kpi_label}</span>
                                <span className="font-mono">{a.current_value || 0}/{a.target_value || 0} {a.kpi_unit || ''}</span>
                              </div>
                              <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
                                <div className="h-full bg-teal-500 transition-all" style={{ width: `${pct}%` }} />
                              </div>
                            </div>
                          )}
                          <div className="flex items-center justify-between gap-2 mt-1.5">
                            <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-bold uppercase border ${pr?.c}`}>{pr?.l}</span>
                            {a.responsible && <span className="text-[10px] text-gray-500 truncate">{a.responsible}</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Action dialog */}
      <Dialog open={actionDialog} onOpenChange={(o) => { setActionDialog(o); if (!o) setEditingAction(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingAction ? 'Editar Ação' : 'Nova Ação'}</DialogTitle>
            <DialogDescription>Ação vinculada a uma das 12 dimensões, com KPI monitorável.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitAction} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="ac_title">Título *</Label>
                <Input id="ac_title" value={actionForm.title} onChange={(e) => setActionForm({ ...actionForm, title: e.target.value })} required data-testid="action-title-input" />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="ac_dim">Dimensão *</Label>
                <select id="ac_dim" value={actionForm.dimension_key} onChange={(e) => setActionForm({ ...actionForm, dimension_key: e.target.value })} className="w-full h-10 px-3 rounded-md border border-input bg-white text-sm" required data-testid="action-dimension-select">
                  <option value="">Selecione...</option>
                  {dimensionsCatalog.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
                </select>
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="ac_desc">Descrição</Label>
                <Textarea id="ac_desc" rows={2} value={actionForm.description} onChange={(e) => setActionForm({ ...actionForm, description: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ac_kpi">Nome do KPI</Label>
                <Input id="ac_kpi" placeholder="Ex: % de vagas afirmativas" value={actionForm.kpi_label} onChange={(e) => setActionForm({ ...actionForm, kpi_label: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ac_unit">Unidade</Label>
                <Input id="ac_unit" placeholder="%, número, dias..." value={actionForm.kpi_unit} onChange={(e) => setActionForm({ ...actionForm, kpi_unit: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ac_cur">Valor atual</Label>
                <Input id="ac_cur" type="number" step="0.01" value={actionForm.current_value} onChange={(e) => setActionForm({ ...actionForm, current_value: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ac_tgt">Valor meta</Label>
                <Input id="ac_tgt" type="number" step="0.01" value={actionForm.target_value} onChange={(e) => setActionForm({ ...actionForm, target_value: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ac_pri">Prioridade</Label>
                <select id="ac_pri" value={actionForm.priority} onChange={(e) => setActionForm({ ...actionForm, priority: e.target.value })} className="w-full h-10 px-3 rounded-md border border-input bg-white text-sm">
                  {PRIORITIES.map((p) => <option key={p.v} value={p.v}>{p.l}</option>)}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="ac_st">Status</Label>
                <select id="ac_st" value={actionForm.status} onChange={(e) => setActionForm({ ...actionForm, status: e.target.value })} className="w-full h-10 px-3 rounded-md border border-input bg-white text-sm">
                  {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="ac_resp">Responsável</Label>
                <Input id="ac_resp" value={actionForm.responsible} onChange={(e) => setActionForm({ ...actionForm, responsible: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ac_date">Prazo</Label>
                <Input id="ac_date" type="date" value={actionForm.due_date} onChange={(e) => setActionForm({ ...actionForm, due_date: e.target.value })} />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setActionDialog(false)}>Cancelar</Button>
              <Button type="submit" data-testid="action-save-button">{editingAction ? 'Atualizar' : 'Criar'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Remover ação</AlertDialogTitle><AlertDialogDescription>Ação permanente.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleteAction(deleteId)}>Confirmar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Report dialog */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><History className="text-teal-600" size={18} /> Histórico de Relatórios</DialogTitle>
            <DialogDescription>Todas as versões geradas para este diagnóstico. A mais recente é exibida no portal do cliente.</DialogDescription>
          </DialogHeader>
          {historyLoading ? (
            <div className="py-8 text-center"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600 mx-auto"></div></div>
          ) : historyVersions.length === 0 ? (
            <div className="py-8 text-center text-sm text-gray-500">Nenhum relatório gerado ainda.</div>
          ) : (
            <div className="space-y-2">
              {historyVersions.map((v, i) => (
                <div key={v.id} className="flex items-center justify-between p-3 border rounded-lg bg-white hover:shadow-sm" data-testid={`history-item-${v.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-gray-900">Versão {historyVersions.length - i}</span>
                      {i === 0 && <span className="inline-block px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-bold uppercase">Atual</span>}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {new Date(v.generated_at).toLocaleString('pt-BR')}
                      {v.generated_by && ` · por ${v.generated_by}`}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">Score {v.overall_score?.toFixed(2)} · {v.maturity_stage}</div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button variant="ghost" size="sm" onClick={() => openVersion(v.id)} data-testid={`view-version-${v.id}`}><Eye className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => setDeleteVersionId(v.id)} data-testid={`delete-version-${v.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteVersionId} onOpenChange={() => setDeleteVersionId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir esta versão do relatório?</AlertDialogTitle>
            <AlertDialogDescription>Se for a versão atual, a mais recente restante será promovida automaticamente. Ação permanente.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleteVersion(deleteVersionId)} data-testid="confirm-delete-version">Confirmar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Client portal token dialog */}
      <Dialog open={tokenDialog} onOpenChange={setTokenDialog}>
        <DialogContent className="max-w-lg" data-testid="client-token-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><LinkIcon className="text-teal-600" size={18} /> Link do Portal do Cliente</DialogTitle>
            <DialogDescription>Gere um link único e seguro para esta empresa acompanhar seu dashboard em tempo real (radar de maturidade, plano de ação e relatório).</DialogDescription>
          </DialogHeader>
          {tokenLoading ? (
            <div className="py-8 text-center"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600 mx-auto"></div></div>
          ) : (
            <div className="space-y-3">
              {tokenInfo?.enabled ? (
                <>
                  <div className="flex items-center gap-2 text-xs">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-bold uppercase">Ativo</span>
                  </div>
                  <div className="flex gap-2">
                    <Input readOnly value={clientFullUrl} data-testid="client-token-url" className="font-mono text-xs" />
                    <Button size="sm" variant="outline" onClick={copyClientLink} data-testid="copy-client-link"><Copy size={14} /></Button>
                  </div>
                  <div className="text-xs text-gray-500">Qualquer pessoa com este link acessa o portal. Revogue para invalidá-lo.</div>
                  <div className="flex justify-end gap-2 pt-2">
                    <Button variant="outline" size="sm" onClick={revokeToken} data-testid="revoke-token-button"><ShieldOff className="mr-1 h-3 w-3" /> Revogar acesso</Button>
                    <Button size="sm" onClick={generateToken} data-testid="regenerate-token-button">Gerar novo link</Button>
                  </div>
                </>
              ) : (
                <div className="text-center py-4">
                  <div className="text-sm text-gray-600 mb-3">Nenhum link ativo para esta empresa.</div>
                  <Button onClick={generateToken} data-testid="generate-token-button"><LinkIcon className="mr-2 h-4 w-4" /> Gerar link do cliente</Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Report dialog */}
      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="report-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="text-purple-600" size={20} /> Relatório de Diagnóstico</DialogTitle>
            <DialogDescription>Gerado com IA (Gemini 3.1 Pro) — revise antes de entregar ao cliente.</DialogDescription>
          </DialogHeader>
          {generating ? (
            <div className="py-16 text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600 mx-auto mb-4"></div>
              <div className="text-sm text-gray-600">Consolidando dados e gerando relatório completo...</div>
              <div className="text-xs text-gray-400 mt-2">Pode levar 30-60 segundos</div>
            </div>
          ) : report ? (
            <>
              <div className="flex gap-2 mb-3 flex-wrap">
                <Button variant="outline" size="sm" onClick={copyReport} data-testid="report-copy-button"><Copy className="mr-1 h-3 w-3" /> Copiar Markdown</Button>
                <Button variant="outline" size="sm" onClick={printReport} data-testid="report-print-button"><Printer className="mr-1 h-3 w-3" /> Imprimir</Button>
                <Button
                  size="sm"
                  onClick={downloadPdf}
                  disabled={!reportVersionId || downloadingPdf}
                  className="bg-teal-600 hover:bg-teal-700"
                  data-testid="report-download-pdf-button"
                >
                  <Download className="mr-1 h-3 w-3" />
                  {downloadingPdf ? 'Gerando PDF...' : 'Baixar PDF'}
                </Button>
              </div>
              <div className="prose prose-sm max-w-none prose-headings:text-gray-900 prose-a:text-teal-600 prose-strong:text-gray-900 prose-table:text-xs" data-testid="report-content">
                <ReactMarkdown>{report}</ReactMarkdown>
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </AdminSidebar>
  );
}
